import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  WRITE_OFF_MODAL,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { EventBus } from '../systems/EventBus';
import { renderHelpLink } from './HelpLink';
import { propagateDepth } from './propagateDepth';

const INVOICE_ID = 'INV-3002';
const CUSTOMER = 'Delta Retail';
const AMOUNT = 800;

export class WriteOffModal {
  private container: Phaser.GameObjects.Container | null = null;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;

  private readonly onBeat = (p: { beatId: string }) => {
    if (p.beatId === 'beat-7-write-off' && !this.container) this.open();
  };

  constructor(private readonly scene: Phaser.Scene, private readonly bus: EventBus) {
    bus.on('beat:triggered', this.onBeat);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  approveForTest(): void {
    this.emitApproved();
    this.close();
  }

  declineForTest(): void {
    this.emitDeclined();
    this.close();
  }

  private open(): void {
    this.bus.emit('term:unlocked', { term: 'write-off' });
    this.bus.emit('write-off:opened', { invoiceId: INVOICE_ID, customer: CUSTOMER, amount: AMOUNT });

    const x = Math.round((GAME_WIDTH - WRITE_OFF_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - WRITE_OFF_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, WRITE_OFF_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, WRITE_OFF_MODAL.width, WRITE_OFF_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const title = this.scene.add.text(
      x + WRITE_OFF_MODAL.paddingX,
      y + WRITE_OFF_MODAL.paddingY,
      COPY.writeOff.title,
      {
        fontFamily: FONTS.ui,
        fontSize: WRITE_OFF_MODAL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    renderHelpLink(
      this.scene,
      root,
      x + WRITE_OFF_MODAL.width - WRITE_OFF_MODAL.paddingX,
      y + WRITE_OFF_MODAL.paddingY + 4,
      'write-off',
      this.bus,
    );

    const subtitleY = y + WRITE_OFF_MODAL.paddingY + title.height + 6;
    const subtitle = this.scene.add.text(
      x + WRITE_OFF_MODAL.paddingX,
      subtitleY,
      COPY.writeOff.subtitle,
      {
        fontFamily: FONTS.ui,
        fontSize: WRITE_OFF_MODAL.subtitleFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(subtitle);

    const consequenceY = subtitleY + subtitle.height + WRITE_OFF_MODAL.consequencePaddingY;
    const consequence = this.scene.add.text(
      x + WRITE_OFF_MODAL.paddingX,
      consequenceY,
      COPY.writeOff.consequence,
      {
        fontFamily: FONTS.ui,
        fontSize: WRITE_OFF_MODAL.consequenceFontSize,
        color: toHexColor(COLORS.accentWarn),
        fontStyle: 'bold',
        wordWrap: { width: WRITE_OFF_MODAL.width - WRITE_OFF_MODAL.paddingX * 2 },
      },
    );
    root.add(consequence);

    const bodyY = consequenceY + consequence.height + WRITE_OFF_MODAL.consequencePaddingY;
    const body = this.scene.add.text(
      x + WRITE_OFF_MODAL.paddingX,
      bodyY,
      COPY.writeOff.body,
      {
        fontFamily: FONTS.ui,
        fontSize: WRITE_OFF_MODAL.bodyFontSize,
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: WRITE_OFF_MODAL.width - WRITE_OFF_MODAL.paddingX * 2 },
      },
    );
    root.add(body);

    const buttonY = y + WRITE_OFF_MODAL.height - WRITE_OFF_MODAL.paddingY - WRITE_OFF_MODAL.buttonHeight;
    const buttonAreaWidth = WRITE_OFF_MODAL.width - WRITE_OFF_MODAL.paddingX * 2;
    const halfWidth = (buttonAreaWidth - WRITE_OFF_MODAL.buttonGap) / 2;

    const approveButton = this.scene.add
      .rectangle(
        x + WRITE_OFF_MODAL.paddingX,
        buttonY,
        halfWidth,
        WRITE_OFF_MODAL.buttonHeight,
        COLORS.buttonPrimary,
      )
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    root.add(approveButton);

    const approveLabel = this.scene.add
      .text(
        x + WRITE_OFF_MODAL.paddingX + halfWidth / 2,
        buttonY + WRITE_OFF_MODAL.buttonHeight / 2,
        COPY.writeOff.approveButton,
        {
          fontFamily: FONTS.ui,
          fontSize: WRITE_OFF_MODAL.buttonFontSize,
          color: toHexColor(COLORS.buttonPrimaryText),
          fontStyle: 'bold',
        },
      )
      .setOrigin(0.5, 0.5);
    root.add(approveLabel);

    approveButton.on('pointerdown', () => {
      this.emitApproved();
      this.showToast(COPY.writeOff.toastApproved);
      this.close();
    });

    const declineX = x + WRITE_OFF_MODAL.paddingX + halfWidth + WRITE_OFF_MODAL.buttonGap;
    const declineButton = this.scene.add
      .rectangle(declineX, buttonY, halfWidth, WRITE_OFF_MODAL.buttonHeight, COLORS.windowChrome)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive({ useHandCursor: true });
    root.add(declineButton);

    const declineLabel = this.scene.add
      .text(
        declineX + halfWidth / 2,
        buttonY + WRITE_OFF_MODAL.buttonHeight / 2,
        COPY.writeOff.declineButton,
        {
          fontFamily: FONTS.ui,
          fontSize: WRITE_OFF_MODAL.buttonFontSize,
          color: toHexColor(COLORS.textPrimary),
        },
      )
      .setOrigin(0.5, 0.5);
    root.add(declineLabel);

    declineButton.on('pointerdown', () => {
      this.emitDeclined();
      this.showToast(COPY.writeOff.toastDeclined);
      this.close();
    });

    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.close();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private emitApproved(): void {
    this.bus.emit('write-off:approved', { invoiceId: INVOICE_ID, customer: CUSTOMER, amount: AMOUNT });
  }

  private emitDeclined(): void {
    this.bus.emit('write-off:declined', { invoiceId: INVOICE_ID, customer: CUSTOMER, amount: AMOUNT });
  }

  private showToast(message: string): void {
    const toast = this.scene.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 80,
      message,
      {
        fontFamily: FONTS.ui,
        fontSize: WRITE_OFF_MODAL.toastFontSize,
        color: toHexColor(COLORS.textPrimary),
        backgroundColor: toHexColor(COLORS.windowChrome),
        padding: { x: 12, y: 8 },
      },
    ).setOrigin(0.5, 0.5).setDepth(DEPTHS.modal);

    this.scene.time.delayedCall(WRITE_OFF_MODAL.toastDurationMs, () => toast.destroy());
  }

  private close(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  private teardown(): void {
    this.bus.off('beat:triggered', this.onBeat);
    this.close();
  }
}
